// client/src/lib/filters/contactFilters.ts
import { where, QueryConstraint } from 'firebase/firestore';
import type { ContactSource } from '../../types/lead';

export interface ContactFilters {
  source?: ContactSource;
  companyName?: string;
}

export interface BuiltFilters {
  whereClauses: QueryConstraint[];
  typesenseFilterBy: string;
}

export interface BuildContactFilterOptions {
  /**
   * Whether the current user may see recruiting candidates (P14/Recruiting).
   * When `false`, a positive `isCandidate == false` clause is appended so
   * recruiting candidates never leak into general contact lists (#2559).
   *
   * REQUIRED (no default) — every caller must make an explicit access decision.
   * A silent default here would be a fail-open footgun: a future call site that
   * forgot to thread the flag would leak candidates with no compiler/lint signal.
   */
  canViewCandidates: boolean;
}

/**
 * Firestore constraint(s) that exclude recruiting candidates from general
 * contact lists for users without P14 (Recruiting) access (#2559).
 *
 * POSITIVE equality (`isCandidate == false`) — NEVER a `source`/profile
 * negation. Firestore cannot express "field != x OR field-absent", so a
 * negation would silently drop every contact missing the field. Every contact
 * now carries an explicit boolean `isCandidate` (prod backfill 2026-07-20 +
 * write-time default in the Cloud Function contact creators), so this equality
 * returns every non-candidate and omits only true candidates. P14 users pass
 * `canViewCandidates = true` and get the unfiltered list.
 */
export function candidateExclusionClauses(canViewCandidates: boolean): QueryConstraint[] {
  return canViewCandidates ? [] : [where('isCandidate', '==', false)];
}

/**
 * Single source of truth for how contact list filters translate into
 * Firestore `where` clauses and Typesense `filterBy` expressions.
 *
 * Used by both `usePaginatedContacts` (Firestore list query) and
 * `<PageSearch collection="contacts">` (Typesense query) so the two
 * stay in sync automatically.
 */
export function buildContactFilters(
  filters: ContactFilters,
  opts: BuildContactFilterOptions
): BuiltFilters {
  const whereClauses: QueryConstraint[] = [];
  const typesenseParts: string[] = [];

  if (filters.source) {
    whereClauses.push(where('source', '==', filters.source));
    typesenseParts.push(`source:=\`${filters.source}\``);
  }

  if (filters.companyName) {
    whereClauses.push(where('companyName', '==', filters.companyName));
    // Backtick-quote string values to handle spaces and special characters.
    typesenseParts.push(`companyName:=\`${filters.companyName}\``);
  }

  // Exclude recruiting candidates for users without P14 access (#2559). This is
  // the FIRESTORE list-query enforcement only — the Typesense/search surface for
  // contacts is gated separately and authoritatively in useTypesenseSearch
  // (collection === 'contacts' appends isCandidate:=false for non-P14 users), so
  // adding it to typesenseFilterBy here would be redundant, unconsumed noise.
  whereClauses.push(...candidateExclusionClauses(opts.canViewCandidates));

  return {
    whereClauses,
    typesenseFilterBy: typesenseParts.join(' && '),
  };
}
